import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Table, Button, Spinner, Alert } from 'reactstrap';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const iconChip = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
  background: toRgba(ORANGE_COLORS.primary, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const iconGlyph = {
  color: ORANGE_COLORS.primary,
  fontSize: 18,
};

function ComplianceTable() {
  const [rows, setRows] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Color-coded criticality badges
  const getCriticalityBadgeStyle = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case "critical":
        return { 
          backgroundColor: 'rgba(139, 0, 0, 0.1)', 
          color: '#8b0000',
          border: '1px solid rgba(139, 0, 0, 0.3)'
        };
      case "high":
        return { 
          backgroundColor: 'rgba(220, 53, 69, 0.1)', 
          color: '#dc3545',
          border: '1px solid rgba(220, 53, 69, 0.3)'
        };
      case "medium":
        return { 
          backgroundColor: 'rgba(255, 193, 7, 0.1)', 
          color: '#ffc107',
          border: '1px solid rgba(255, 193, 7, 0.3)'
        };
      case "low":
        return { 
          backgroundColor: 'rgba(25, 135, 84, 0.1)', 
          color: '#198754',
          border: '1px solid rgba(25, 135, 84, 0.3)'
        };
      default:
        return { 
          backgroundColor: 'rgba(108, 117, 125, 0.1)', 
          color: '#6c757d',
          border: '1px solid rgba(108, 117, 125, 0.3)'
        };
    }
  };

  useEffect(() => {
    const fetchCompliance = async () => {
      setLoading(true);
      setError(null);
      try {
        const authUser = JSON.parse(sessionStorage.getItem("authUser"));
        const headers = {
          "Content-Type": "application/json",
          ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
        };
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const result = await fetch(
          `${API_BASE_URL}/threats/compliance-mappings?limit=500&page=1`, 
          { headers, signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        
        if (!result.ok) {
          throw new Error(`HTTP error! status: ${result.status}`);
        }
        
        const data = await result.json();
        console.log('Compliance Mappings Response:', data);

        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          setRows(data.data);
          const uniqueFrameworks = [...new Set(data.data.map(row => row.framework))].filter(Boolean);
          setFrameworks(uniqueFrameworks);
          setSelectedFramework(uniqueFrameworks[0] || "");
        } else {
          setError('No compliance data available');
          setRows([]);
          setFrameworks([]);
        }
      } catch (err) {
        console.error("Failed to fetch compliance mappings:", err);
        if (err.name === 'AbortError') {
          setError('Request timed out. Please try again.');
        } else {
          setError(err.message);
        }
        setRows([]);
        setFrameworks([]);
        setSelectedFramework("");
      } finally {
        setLoading(false);
      }
    };
    fetchCompliance();
  }, []);

  const filteredRows = selectedFramework
    ? rows.filter(row => row.framework === selectedFramework)
    : [];

  const rowsPerPage = 10;
  const start = (page - 1) * rowsPerPage;
  const currentRows = filteredRows.slice(start, start + rowsPerPage);
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  if (loading) {
    return (
      <Card style={cardShell}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-file-list-3-line" style={iconGlyph}></i>
            </span>
            Compliance Mappings
          </h5>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card style={cardShell}>
      <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-file-list-3-line" style={iconGlyph}></i>
            </span>
            Compliance Mappings
          </h5>
          {rows.length > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`
              }}
            >
              {rows.length} records
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <Alert style={{ 
            backgroundColor: ORANGE_COLORS.subtle, 
            color: ORANGE_COLORS.darker, 
            border: `1px solid ${ORANGE_COLORS.muted}` 
          }}>
            <i className="ri-alert-line me-2"></i>
            {error}
          </Alert>
        )}

        {frameworks.length > 0 ? (
          <>
            <div className="mb-3">
              <label className="form-label fw-medium">Select Framework:</label>
              <select 
                className="form-select" 
                value={selectedFramework}
                onChange={(e) => { 
                  setSelectedFramework(e.target.value); 
                  setPage(1); 
                }}
              >
                {frameworks.map((item, index) => (
                  <option key={index} value={item}>{item}</option>
                ))}
              </select>
              <small className="text-muted">
                Showing {filteredRows.length} mappings for {selectedFramework}
              </small>
            </div>

            <div className="table-responsive">
              <Table className="table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Framework</th>
                    <th>Control ID</th>
                    <th>Description</th>
                    <th>Agent</th>
                    <th className="text-center">Criticality</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? (
                    currentRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <span 
                            className="badge" 
                            style={{ 
                              backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                              color: ORANGE_COLORS.primary,
                              border: `1px solid ${ORANGE_COLORS.primary}`
                            }}
                          >
                            {row.framework}
                          </span>
                        </td>
                        <td>
                          <code style={{ color: ORANGE_COLORS.dark }}>{row.control_id}</code>
                        </td>
                        <td style={{ maxWidth: "400px" }}>
                          {row.threat_description || 'N/A'}
                        </td>
                        <td>{row.agent_name || 'N/A'}</td>
                        <td className="text-center">
                          <span 
                            className="badge" 
                            style={getCriticalityBadgeStyle(row.asset_criticality)}
                          >
                            {row.asset_criticality || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No compliance data available for selected framework
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    style={{ 
                      backgroundColor: page === 1 ? '#f0f0f0' : ORANGE_COLORS.primary, 
                      borderColor: page === 1 ? '#ccc' : ORANGE_COLORS.primary,
                      color: page === 1 ? '#999' : 'white'
                    }}
                  >
                    <i className="ri-arrow-left-line me-1"></i>Previous
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    style={{ 
                      backgroundColor: page === totalPages ? '#f0f0f0' : ORANGE_COLORS.primary, 
                      borderColor: page === totalPages ? '#ccc' : ORANGE_COLORS.primary,
                      color: page === totalPages ? '#999' : 'white'
                    }}
                  >
                    Next<i className="ri-arrow-right-line ms-1"></i>
                  </Button>
                </div>
                <div className="text-muted" style={{ fontSize: "13px" }}>
                  Page {page} of {totalPages}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <span style={{ ...iconChip, width: 48, height: 48, margin: "0 auto 12px" }}>
              <i className="ri-file-list-3-line" style={iconGlyph}></i>
            </span>
            <p className="text-muted">No compliance frameworks available</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default ComplianceTable;
